import React, { useMemo, useState } from 'react';
import { CalendarDay, TourCalendarEntry, Tour } from '../../api/types';
import { useUi } from '../../i18n/ui';
import { resolveTour } from '../../i18n/tour';
import { euro } from '../../utils/text';

interface Props {
  days: CalendarDay[];
  tours: Tour[];
  onPick: (entry: TourCalendarEntry, date: string) => void;
  months?: number;
  loading?: boolean;
}

/** Palette per colorare i pacchetti nel calendario (coerente col tema dark/acid). */
const PALETTE = ['#E4FF1A', '#4FC3F7', '#FF8A65', '#BA68C8', '#81C784', '#FFD54F', '#F06292', '#A1887F'];
const MAX_DOTS = 4;

const pad = (n: number) => String(n).padStart(2, '0');
const iso = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const parseISO = (s: string) => { const [y, m, d] = s.split('-').map(Number); return new Date(y, m - 1, d); };
/** Indice del giorno della settimana con lunedì = 0. */
const weekdayMon = (d: Date) => (d.getDay() + 6) % 7;

/** Partenza fissa (slot SCHEDULED) come intervallo continuo di date. */
interface Span {
  slotId: number;
  tourId: number;
  entry: TourCalendarEntry;
  start: string; // ISO
  end: string;   // ISO
  soldOut: boolean;
  availableSeats: number;
  price: number;
}

/** Segmento di una barra ristretto a una settimana. */
interface Segment {
  span: Span;
  colStart: number; // 1..7
  colEnd: number;   // 1..7
  isStart: boolean; // contiene la data di partenza reale
  isEnd: boolean;   // contiene la data di fine reale
  lane: number;
}

const TourShowcaseCalendar: React.FC<Props> = ({ days, tours, onPick, months = 2, loading }) => {
  const { tr, lang, months: MONTHS, weekdays: WEEKDAYS, dateLocale } = useUi();

  const byDate = useMemo(() => {
    const m = new Map<string, CalendarDay>();
    days.forEach((d) => m.set(d.date, d));
    return m;
  }, [days]);

  // Titolo per tour: titolo (localizzato) dalla lista tour, con fallback al titolo del calendario.
  const titleOf = useMemo(() => {
    const m = new Map<number, string>();
    days.forEach((d) => d.tours.forEach((e) => { if (!m.has(e.tourId)) m.set(e.tourId, e.tourTitle); }));
    tours.forEach((t) => m.set(t.id, resolveTour(t, lang).title));
    return m;
  }, [tours, lang, days]);

  // Colore stabile per ciascun tour presente nel calendario.
  const colorOf = useMemo(() => {
    const ids = Array.from(new Set(days.flatMap((d) => d.tours.map((e) => e.tourId)))).sort((a, b) => a - b);
    const m = new Map<number, string>();
    ids.forEach((id, i) => m.set(id, PALETTE[i % PALETTE.length]));
    return m;
  }, [days]);

  const legend = useMemo(
    () => Array.from(colorOf.keys()).map((id) => ({ id, color: colorOf.get(id)!, title: titleOf.get(id) || `#${id}` })),
    [colorOf, titleOf]
  );

  // Ricostruisce le partenze fisse come intervalli continui [start,end] a partire dai giorni.
  const spans = useMemo<Span[]>(() => {
    const bySlot = new Map<number, { entry: TourCalendarEntry; dates: string[] }>();
    days.forEach((d) => d.tours.forEach((e) => {
      if (e.bookingMode !== 'SCHEDULED' || e.slotId == null) return;
      const g = bySlot.get(e.slotId) || { entry: e, dates: [] };
      g.dates.push(d.date);
      bySlot.set(e.slotId, g);
    }));
    const out: Span[] = [];
    bySlot.forEach((g, slotId) => {
      const sorted = g.dates.slice().sort();
      out.push({
        slotId,
        tourId: g.entry.tourId,
        entry: g.entry,
        start: sorted[0],
        end: sorted[sorted.length - 1],
        soldOut: g.entry.soldOut,
        availableSeats: g.entry.availableSeats,
        price: g.entry.pricePerPersonPerDay,
      });
    });
    return out;
  }, [days]);

  const today = useMemo(() => { const t = new Date(); return new Date(t.getFullYear(), t.getMonth(), t.getDate()); }, []);
  const [cursor, setCursor] = useState(() => new Date(today.getFullYear(), today.getMonth(), 1));
  const [selected, setSelected] = useState<string | null>(null);

  /** Pacchetti distinti (per tour+slot) di una giornata, per il pannello di dettaglio. */
  const entriesOf = (dayISO: string): TourCalendarEntry[] => {
    const day = byDate.get(dayISO);
    if (!day) return [];
    const seen = new Set<string>();
    const out: TourCalendarEntry[] = [];
    day.tours.forEach((e) => {
      const key = `${e.tourId}:${e.slotId ?? 'flex'}`;
      if (seen.has(key)) return;
      seen.add(key);
      out.push(e);
    });
    return out;
  };

  /** Pallini dei soli tour FLEXIBLE disponibili in una giornata. */
  const flexDots = (dayISO: string): TourCalendarEntry[] =>
    entriesOf(dayISO).filter((e) => e.bookingMode === 'FLEXIBLE');

  const clickDay = (dayISO: string) => {
    if (entriesOf(dayISO).length === 0) return;
    setSelected(dayISO);
  };

  /** Assegna a ciascun segmento una "corsia" per evitare sovrapposizioni. */
  const assignLanes = (segs: Segment[]): Segment[] => {
    const laneEnd: number[] = []; // ultima colonna occupata per corsia
    segs.sort((a, b) => a.colStart - b.colStart || a.span.start.localeCompare(b.span.start));
    segs.forEach((s) => {
      let lane = 0;
      while (lane < laneEnd.length && laneEnd[lane] >= s.colStart) lane++;
      laneEnd[lane] = s.colEnd;
      s.lane = lane;
    });
    return segs;
  };

  const renderWeek = (week: (Date | null)[], key: string) => {
    const dates = week.filter((c): c is Date => !!c);
    if (!dates.length) return null;
    const weekMin = iso(dates[0]);
    const weekMax = iso(dates[dates.length - 1]);

    // Segmenti delle partenze fisse che intersecano questa settimana.
    const segs: Segment[] = [];
    spans.forEach((sp) => {
      if (sp.start > weekMax || sp.end < weekMin) return;
      const segStart = sp.start > weekMin ? sp.start : weekMin;
      const segEnd = sp.end < weekMax ? sp.end : weekMax;
      segs.push({
        span: sp,
        colStart: weekdayMon(parseISO(segStart)) + 1,
        colEnd: weekdayMon(parseISO(segEnd)) + 1,
        isStart: sp.start >= weekMin,
        isEnd: sp.end <= weekMax,
        lane: 0,
      });
    });
    assignLanes(segs);
    const laneCount = segs.reduce((m, s) => Math.max(m, s.lane + 1), 0);

    return (
      <div className="calg__week" key={key}>
        <div className="calg__days">
          {week.map((cell, i) => {
            if (!cell) return <div key={`e${i}`} className="calg__day calg__day--empty" />;
            const dISO = iso(cell);
            const has = entriesOf(dISO).length > 0;
            const dots = flexDots(dISO);
            const isSel = dISO === selected;
            const cls = [
              'calg__day',
              has ? 'calg__day--has' : 'calg__day--none',
              isSel ? 'calg__day--sel' : '',
            ].filter(Boolean).join(' ');
            return (
              <button type="button" key={dISO} className={cls} disabled={!has} onClick={() => clickDay(dISO)}>
                <span className="cal__num">{cell.getDate()}</span>
                {dots.length > 0 && (
                  <span className="calx__dots">
                    {dots.slice(0, MAX_DOTS).map((e, k) => (
                      <i key={k} className="calx__dot" style={{ background: colorOf.get(e.tourId), opacity: e.soldOut ? 0.35 : 1 }} />
                    ))}
                    {dots.length > MAX_DOTS && <i className="calx__dot calx__dot--more">+</i>}
                  </span>
                )}
              </button>
            );
          })}
        </div>
        {laneCount > 0 && (
          <div className="calg__bars" style={{ gridTemplateRows: `repeat(${laneCount}, 20px)` }}>
            {segs.map((s, i) => {
              const color = colorOf.get(s.span.tourId);
              const cls = [
                'calg__bar',
                s.isStart ? 'calg__bar--start' : '',
                s.isEnd ? 'calg__bar--end' : '',
                s.span.soldOut ? 'calg__bar--full' : '',
              ].filter(Boolean).join(' ');
              return (
                <button
                  type="button"
                  key={i}
                  className={cls}
                  style={{ gridColumn: `${s.colStart} / ${s.colEnd + 1}`, gridRow: s.lane + 1, ['--bar' as any]: color }}
                  onClick={() => onPick(s.span.entry, s.span.start)}
                  title={`${titleOf.get(s.span.tourId) || s.span.entry.tourTitle} · ${s.span.soldOut ? tr('cal.pkg.full') : `${s.span.availableSeats} ${tr('cal.pkg.seats')}`}`}
                >
                  <span className="calg__bar-label">{titleOf.get(s.span.tourId) || s.span.entry.tourTitle}</span>
                  {!s.span.soldOut && <span className="calg__bar-seats">{s.span.availableSeats}</span>}
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const renderMonth = (base: Date) => {
    const year = base.getFullYear();
    const month = base.getMonth();
    const first = new Date(year, month, 1);
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const lead = weekdayMon(first);
    const cells: (Date | null)[] = [];
    for (let i = 0; i < lead; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
    while (cells.length % 7 !== 0) cells.push(null);
    const weeks: (Date | null)[][] = [];
    for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));

    return (
      <div className="cal__month" key={`${year}-${month}`}>
        <div className="cal__month-name">{MONTHS[month]} {year}</div>
        <div className="calg__wd">{WEEKDAYS.map((w) => <div key={w} className="cal__wd">{w}</div>)}</div>
        {weeks.map((w, i) => renderWeek(w, `${year}-${month}-${i}`))}
      </div>
    );
  };

  const monthsToShow = Array.from({ length: months }, (_, i) => new Date(cursor.getFullYear(), cursor.getMonth() + i, 1));
  const canGoPrev = cursor > new Date(today.getFullYear(), today.getMonth(), 1);
  const selEntries = selected ? entriesOf(selected) : [];
  const selLabel = selected
    ? new Date(`${selected}T00:00:00`).toLocaleDateString(dateLocale, { weekday: 'long', day: '2-digit', month: 'long' })
    : '';

  if (loading) {
    return <div className="cal"><p className="cal__loading">{tr('cal.loading')}</p></div>;
  }
  if (!days.length) {
    return <div className="cal"><p className="cal__loading">{tr('cal.showcase.empty')}</p></div>;
  }

  return (
    <div className="cal calx">
      <div className="cal__head">
        <button type="button" className="cal__nav" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))} disabled={!canGoPrev} aria-label="Mese precedente">‹</button>
        <span className="cal__legend">
          <i className="cal__dot cal__dot--free" /> {tr('cal.legend.free')}
          <i className="cal__dot" style={{ background: 'rgba(11,11,12,.28)', marginLeft: 8 }} /> {tr('cal.pkg.full')}
        </span>
        <button type="button" className="cal__nav" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))} aria-label="Mese successivo">›</button>
      </div>

      <div className="cal__months">{monthsToShow.map(renderMonth)}</div>

      {/* Legenda pacchetti */}
      {legend.length > 0 && (
        <div className="calx__legend">
          {legend.map((l) => (
            <span key={l.id} className="calx__legend-item">
              <i className="calx__dot" style={{ background: l.color }} /> {l.title}
            </span>
          ))}
        </div>
      )}

      {/* Dettaglio giornata selezionata */}
      <div className="calx__detail">
        {selected && selEntries.length > 0 ? (
          <>
            <p className="calx__detail-date">{tr('cal.detail.on')} {selLabel}</p>
            <ul className="calx__pkgs">
              {selEntries.map((e, k) => (
                <li key={k} className="calx__pkg">
                  <span className="calx__pkg-color" style={{ background: colorOf.get(e.tourId) }} />
                  <span className="calx__pkg-main">
                    <span className="calx__pkg-title">{titleOf.get(e.tourId) || e.tourTitle}</span>
                    <span className="calx__pkg-meta">
                      {e.soldOut ? tr('cal.pkg.full') : `${e.availableSeats} ${tr('cal.pkg.seats')}`}
                      {e.pricePerPersonPerDay > 0 && ` · ${euro(e.pricePerPersonPerDay)} ${tr('cal.pkg.perday')}`}
                    </span>
                  </span>
                  <button type="button" className="btn btn-acid calx__pkg-btn" onClick={() => onPick(e, selected)}>
                    {tr('cal.pkg.book')}
                  </button>
                </li>
              ))}
            </ul>
          </>
        ) : (
          <p className="calx__detail-prompt">{tr('cal.detail.prompt')}</p>
        )}
      </div>
    </div>
  );
};

export default TourShowcaseCalendar;
